import XLSX from 'xlsx-js-style';
import type { CommissionType } from '../../api/salesCommision';
import type { EtcBonusAmountType } from '../../api/salaryCalc';
import type { ComputeSalaryResult } from './computeSalary';

/**
 * 강사별 급여 엑셀의 요약 칸(1~2행) — 화면 숫자(computeSalary 결과)를 그대로 숫자로 쓴다.
 *
 * 예전에는 요약 칸을 엑셀 수식으로 다시 만들었다.
 * - 매출(B2) = 판매 내역의 결제합 − 취소합 → 그룹 매출·매출 조정이 빠졌다.
 * - 인센티브(C2) = PRODUCT(B2, 비율) → 화면의 원 단위 내림(floor)과 달랐다.
 * - 최종 급여 = 요약 칸 SUM → 위 두 값이 틀리면 같이 틀렸다.
 * 지금은 수식을 쓰지 않는다. 엑셀 최종 급여 = 화면 최종 급여.
 */

export type CoachSalaryExcelEtcBonus = {
    name: string;
    /** 원 금액(비율 항목도 환산 후) */
    amount: number;
    amountType?: EtcBonusAmountType;
    /** 비율 항목일 때 입력 비율(제목 표기용) */
    percentValue?: number;
};

export type CoachSalaryExcelValue = {
    coachName: string;
    /** 매출(computeSalary.settlement) */
    settlement: number;
    /** 입력한 인센티브(비율이면 %, 고정이면 원) — 제목·참고 칸 표기용 */
    incentive: number;
    incentiveType: CommissionType;
    /** 인센티브 원 금액(computeSalary.incentiveKRW) */
    incentiveKRW: number;
    classFee: number;
    groupScheduleFee?: number;
    groupSales?: number;
    baseSalary: number;
    etcBonus: CoachSalaryExcelEtcBonus[];
    /** 계산된 공제액(과세 여부 무관) */
    deduction: number;
    /** 실제 차감된 공제(과세일 때만, 아니면 0) */
    deductionApplied: number;
    finalSalary: number;
    isTaxable: boolean;
};

/** 화면 상태 + computeSalary 결과 → 엑셀 값. 숫자는 전부 computeSalary 결과에서 온다. */
export const buildCoachSalaryExcelValue = ({
    coachName,
    salary,
    incentive,
    incentiveType,
    etcBonus,
    groupSales,
    groupScheduleFee,
}: {
    coachName: string;
    salary: ComputeSalaryResult;
    incentive: number;
    incentiveType: CommissionType;
    /** computeSalary 에 넣은 기타 수당 목록(같은 순서) */
    etcBonus: { name: string; amount: number; amountType?: EtcBonusAmountType }[];
    groupSales?: number;
    groupScheduleFee?: number;
}): CoachSalaryExcelValue => ({
    coachName,
    settlement: salary.settlement,
    incentive,
    incentiveType,
    incentiveKRW: salary.incentiveKRW,
    classFee: salary.classFee,
    groupScheduleFee,
    groupSales,
    baseSalary: salary.baseSalary,
    etcBonus: etcBonus.map((bonus, index) => ({
        name: bonus.name,
        amount: salary.etcBonusItemsKRW[index] ?? 0,
        amountType: bonus.amountType,
        percentValue: bonus.amountType === 'percentage' ? bonus.amount : undefined,
    })),
    deduction: salary.deduction,
    deductionApplied: salary.deductionApplied,
    finalSalary: salary.finalSalary,
    isTaxable: salary.isTaxable,
});

/** 요약 칸 제목·값(1행 제목, 2행 값). 값은 전부 숫자다(강사 이름 제외). */
export const buildCoachSalaryExcelSummary = (value: CoachSalaryExcelValue): { titles: string[]; values: (string | number)[] } => {
    const items: { title: string; value: string | number }[] = [
        { title: '강사이름', value: value.coachName },
        { title: '매출액', value: value.settlement },
        {
            title: value.incentiveType === 'percentage' ? `매출 인센티브 (${value.incentive} %)` : '매출 인센티브',
            value: value.incentiveKRW,
        },
        { title: '수업료', value: value.classFee },
        { title: '기본급', value: value.baseSalary },
        // 실제 차감액의 부호를 뒤집어 쓴다(비과세는 0). 공제가 음수(매출 조정으로 인센티브가 음수)면 칸은 양수가 된다
        // — 그래야 요약 항목 합 = 최종 급여. Math.abs 를 쓰지 않는다. -0 이 찍히지 않게 0 을 따로 둔다.
        { title: '3.3% 공제', value: value.deductionApplied ? -value.deductionApplied : 0 },
        ...value.etcBonus.map((item) => ({
            title:
                item.amountType === 'percentage' ? `${item.name} (센터 매출의 ${item.percentValue ?? ''}%)` : item.name,
            value: item.amount,
        })),
        { title: '최종 급여액', value: value.finalSalary },
    ];
    return { titles: items.map((item) => item.title), values: items.map((item) => item.value) };
};

/** 요약 칸만 담은 시트. 비율 인센티브면 C3·C4 에 비율을 참고로 적는다(계산에는 쓰지 않는다). */
export const buildCoachSalarySummarySheet = (value: CoachSalaryExcelValue): XLSX.WorkSheet => {
    const { titles, values } = buildCoachSalaryExcelSummary(value);
    const sheet = XLSX.utils.aoa_to_sheet([titles, values]);
    if (value.incentiveType === 'percentage') {
        XLSX.utils.sheet_add_aoa(sheet, [['매출 인센티브 비율(%)'], [value.incentive]], { origin: 'C3' });
    }
    return sheet;
};

/** 판매 내역 아래 참고 합계 행의 이름 — 요약 「매출액」과 다를 수 있다는 것을 밝힌다. */
export const SALES_LIST_REFERENCE_TOTAL_LABEL = '판매 내역 합계(참고 — 그룹 매출·매출 조정은 위 매출에 포함)';
